import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";
import StandardCTA from "@/components/site/StandardCTA";
import { ARTICLES, getArticleBySlug } from "@/data/articles";
import { RESOURCES_IMAGES } from "@/lib/site";

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="article-not-found">
        <Navbar />
        <section className="pt-[160px] pb-24 mx-auto max-w-[720px] px-6 text-center">
          <div className="eyebrow-intelligence">NOT FOUND</div>
          <h1 className="font-display mt-4 text-platinum text-[36px] leading-tight">
            This article couldn't be found.
          </h1>
          <p className="mt-4 text-[14.5px] text-platinum/60 leading-[1.7]">
            The article may have been moved or removed.
          </p>
          <button
            onClick={() => navigate("/resources")}
            data-testid="article-back-to-resources"
            className="btn-primary rounded-md h-12 mt-8 px-6 text-[14px] font-medium inline-flex items-center gap-2"
          >
            Back to Resources <ArrowRight className="w-4 h-4" />
          </button>
        </section>
        <Footer />
      </div>
    );
  }

  const otherArticles = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div
      className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden"
      data-testid={`article-page-${slug}`}
    >
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={26} tone="mixed" density={0.5} />
      </div>
      <Navbar />

      {/* ============ ARTICLE HEADER ============ */}
      <section className="relative pt-[130px] lg:pt-[160px] pb-10 lg:pb-14 overflow-hidden">
        <div
          className="absolute -top-32 right-0 w-[720px] h-[720px] pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(96,165,250,0.18), transparent)",
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-[820px] px-6 lg:px-8">
          <Link
            to="/resources"
            data-testid="article-back-link"
            className="inline-flex items-center gap-1.5 text-[12.5px] text-platinum/60 hover:text-sapphire-glow transition-colors mb-8 tracking-[0.08em]"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Resources
          </Link>

          <div className="eyebrow-intelligence">ESTHERA · PERSPECTIVES</div>

          <h1 className="font-display mt-5 text-platinum text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.08] tracking-[-0.020em]">
            {article.title}
          </h1>

          <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/68 leading-[1.7] italic">
            {article.excerpt}
          </p>
        </div>
      </section>

      {/* Sapphire hairline separator */}
      <div className="relative mx-auto max-w-[820px] px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
      </div>

      {/* ============ ARTICLE BODY ============ */}
      <article className="relative pt-10 lg:pt-14 pb-16 lg:pb-20">
        <div className="relative mx-auto max-w-[720px] px-6 lg:px-8">
          <div className="space-y-6 text-[15.5px] sm:text-[16px] text-platinum/78 leading-[1.85]">
            {article.body.map((block, i) => {
              if (typeof block === "string") {
                if (i === 0) {
                  return (
                    <p key={i} className="luxe-dropcap">
                      {block}
                    </p>
                  );
                }
                return <p key={i}>{block}</p>;
              }
              // Subhead block
              return (
                <div key={i} className="pt-3">
                  <h3 className="font-display text-platinum text-[20px] sm:text-[22px] leading-tight tracking-[-0.012em] mb-3">
                    <span
                      className="text-sapphire-glow"
                      style={{ textShadow: "0 0 20px rgba(96,165,250,0.40)" }}
                    >
                      {block.heading}
                    </span>
                  </h3>
                  <p>{block.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </article>

      {/* ============ MORE FROM ESTHERA ============ */}
      {otherArticles.length > 0 && (
        <section className="relative pb-16 lg:pb-20">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent mb-12" />
            <div className="text-center fade-up mb-10">
              <div className="eyebrow-intelligence">MORE FROM ESTHERA</div>
              <h2 className="font-display mt-4 text-platinum text-[24px] sm:text-[30px] leading-tight tracking-[-0.018em]">
                Continue reading
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
              {otherArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/resources/${a.slug}`}
                  data-testid={`related-article-${a.slug}`}
                  className="group relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-sapphire-glow/40 transition-all duration-500 flex flex-col p-6 lg:p-7"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(15,22,40,0.55) 0%, rgba(10,15,26,0.92) 100%)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 0 30px -10px rgba(96,165,250,0.40)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
                  <h3 className="font-display text-platinum text-[16px] leading-[1.28] tracking-tight">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-[12.5px] text-platinum/62 leading-[1.6] flex-1">
                    {a.excerpt}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sapphire-glow text-[12px] font-medium tracking-wide group-hover:gap-3 transition-all">
                    Read <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ FINAL CTA ============ */}
      <StandardCTA
        testId="article-cta"
        eyebrow="GET STARTED"
        eyebrowTone="positioning"
        title="Ready to see where your operation leaks?"
        highlight="where your operation leaks?"
        subtitle="Every engagement begins with an Operations Audit — a precise diagnostic of how inquiries actually move through your business today."
        crystal={RESOURCES_IMAGES.ctaCrystal}
        primaryLabel="Get Your Audit Report"
        primaryHref="/get-your-audit-report"
        secondaryLabel="Book a Demo"
        secondaryHref="/book-demo"
      />

      <Footer />
    </div>
  );
}
